<template>
  <div class="m-select-control-tree-level">
    <div
      v-for="(chunk, chunkId) in chunkedChoices"
      :key="chunkId"
      v-observe-visibility="isVisible ? getObserverConfig(chunkId) : false"
      tabindex="0"
      class="m-lazy-render__chunk no-focus-visible"
      :class="{
        'm-lazy-render__lazy-enabled': lazyRender,
        'm-lazy-render__chunk_active': lazyRender && selectedItem && chunkId === selectedItem.chunkId,
        'm-lazy-render__chunk_visible': lazyRender && chunkIsVisible(chunkId)
      }"
      :data-id="chunkId"
      :style="getCssVarsForChunk(chunkId)"
      @keydown.stop="$emit('handle-click-navigation', $event)"
    >
      <template v-if="!chunkIsVisible(chunkId)">
        <div class="m-lazy-render__lazy-dummy-node" />
      </template>
      <template v-else>
        <template v-for="(choice, index) in chunk">
          <template v-if="choiceWithChild(choice)">
            <div
              :key="`sticky-trigger-${depth}-${index}`"
              :class="getStickyTriggerClass(index)"
            />
            <MSelectControlOption
              :id="choice[idAttr]"
              :key="choice[idAttr]"
              :select-is-open="isVisible"
              :style="cssVars"
              :class="getHeaderClassName(choice)"
              :is-active="isActive(choice)"
              :payload="choice"
              :text-key="textAttr"
              :icon-key="iconKey"
              :label-key="labelKey"
              :description-key="descriptionKey"
              :large-with-icon="largeWithIcon"
              :show-icon="true"
              :large="large"
              :bottom-btn-text="choice.bottomBtnText"
              :is-optgroup="!parentIsOption"
              :indent="initialIndent"
              :sticky-trigger-class="getStickyTriggerClass(index)"
              :is-stickable="true"
              :root-el-class="rootElClass"
              :child-is-sticky="childIsSticky"
              :format-fn="formatFn"
              :sticky-elem-height="headerStickyElemHeight"
              class="m-select-control-tree-level__sticky m-select-control-tree-level__header"
              @triggerSticky="handleOptionStickyChange($event, index)"
              @clickOption="activateParentChoice({ ...choice, chunkId: chunkId })"
              @clickBottomBtn="handleClickBottomBtn(choice)"
            />
            <MSelectControlTreeLevel
              :key="`${choice[idAttr]}-child`"
              :is-visible="isVisible"
              :choices="choice[childKey]"
              :id-attr="idAttr"
              :text-attr="textAttr"
              :icon-key="iconKey"
              :show-icon="true"
              :show-id="showId"
              :large="large"
              :initial-indent="childIndent"
              :selected="selected"
              :parent-is-option="parentIsOption"
              :indent-per-level="indentPerLevel"
              :parent-header-offset="childHeaderElOffset"
              :header-class-name="getHeaderClassName(choice)"
              :depth="depth+1"
              :root-el-class="rootElClass"
              :current-focus-item-id="currentFocusItemId"
              :label-key="labelKey"
              :description-key="descriptionKey"
              :large-with-icon="largeWithIcon"
              :bottom-btn-text="choice.bottomBtnText"
              @clickBottomBtn="handleClickBottomBtn(choice)"
              @triggerSticky="handleStickyChange($event, index)"
              @activate="(item) => activateChoice({ ...item, parentId: choice.id, chunkId: chunkId })"
            />
          </template>
          <template v-else>
            <MSelectControlOption
              :key="choice[idAttr]"
              :select-is-open="isVisible"
              :is-active="isActive(choice)"
              :payload="choice"
              :text-key="textAttr"
              :icon-key="iconKey"
              :id-key="idAttr"
              :show-icon="true"
              :show-id="showId"
              :large="large"
              :is-optgroup="false"
              :indent="initialIndent"
              :format-fn="formatFn"
              :current-focus-item-id="currentFocusItemId"
              :bottom-btn-text="choice.bottomBtnText"
              :label-key="labelKey"
              :description-key="descriptionKey"
              :large-with-icon="largeWithIcon"
              @clickOption="activateChoice({ ...choice, chunkId: chunkId })"
              @clickBottomBtn="handleClickBottomBtn(choice)"
            />
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash';
  import MSelectControlOption from 'components/Inputs/Select/MSelectControl/MSelectControlOption';
  import LazyRenderMixin from 'components/mixins/LazyRenderMixin';
  import findNextStickyAfterFalse from './utils/findPrevStickyAfterFalse';
  _.noConflict();

  const INITIAL_Z_INDEX = 100;
  const MEDIUM_SELECT_OPTION_HEIGHT = 30;
  const LARGE_SELECT_OPTION_HEIGHT = 32;

  export default {
    name: 'MSelectControlTreeLevel',
    components: {MSelectControlOption},
    mixins: [LazyRenderMixin],
    props: {
      choices: {
        type: Array,
        default: Array
      },
      idAttr: {
        type: String,
        default: 'id'
      },
      textAttr: {
        type: String,
        default: 'text'
      },
      iconKey: {
        type: String,
        default: 'icon'
      },
      showIcon: {
        type: Boolean,
        default: false
      },
      large: {
        type: Boolean,
        default: false
      },
      childKey: {
        type: String,
        default: 'children'
      },
      initialIndent: {
        type: Number,
        default: 0
      },
      indentPerLevel: {
        type: Number,
        default: 10
      },
      selected: {
        type: Object,
        default: null
      },
      parentIsOption: {
        type: Boolean,
        default: false
      },
      headerClassName: {
        type: String,
        default: null
      },
      parentHeaderOffset: {
        type: Number,
        default: 0
      },
      isVisible: {
        type: Boolean,
        default: false
      },
      depth: {
        type: Number,
        default: 0
      },
      rootElClass: {
        type: String,
        default: null
      },
      showId: {
        type: Boolean,
        default: false
      },
      formatFn: {
        type: Function,
        default: undefined
      },
      lazyRender: {
        type: Boolean,
        default: false
      },
      selectedItem: {
        type: Object,
        default: null
      },
      currentFocusItemId: {
        type: [Number, String],
        default: null
      },
      descriptionKey: {
        type: String,
        default: null
      },
      labelKey: {
        type: String,
        default: null
      },
      largeWithIcon: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        parentHeaderElHeight: 0,
        childIsSticky: false,
        headerStickyElemHeight: null,
        stickyList: {}
      };
    },
    computed: {
      chunkedChoices () {
        if (this.lazyRender) {
          return LazyRenderMixin.computed.chunkedChoices.call(this);
        }
        return [this.choices];
      },
      childIndent () {
        return this.initialIndent + this.indentPerLevel;
      },
      childHeaderElOffset () {
        return this.parentHeaderElHeight + this.parentHeaderOffset;
      },
      componentZIndex () {
        return INITIAL_Z_INDEX - this.depth;
      },
      cssVars () {
        return {
          '--sticky-top-offset': `${this.childHeaderElOffset}px`,
          '--sticky-z-index': this.componentZIndex
        };
      },
      singleElementHeight () {
        if (this.large) {
          return LARGE_SELECT_OPTION_HEIGHT;
        }
        return MEDIUM_SELECT_OPTION_HEIGHT;
      }
    },
    watch: {
      isVisible () {
        if (this.isVisible) {
          return this.calcHeaderOffset();
        }
      },
      selected: {
        immediate: true,
        handler (val) {
          // initialize selectedItem if select has default value
          if (val && this.depth === 0 && !this.selectedItem) {

            if (this.lazyRender) {
              this.chunkedChoices.forEach((chunk, chunkId) => {
                if (chunk.some(item => item.id === val.id)) {
                  this.activateChoice({...val, chunkId});
                }
              });
            } else {
              this.activateChoice({...val, chunkId: 0});
            }
          }
        }
      }
    },
    methods: {
      handleClickBottomBtn (choice) {
        this.$emit('clickBottomBtn', choice);
      },
      handleOptionStickyChange ({isSticky, el}, idx) {
        this.stickyList[idx] = {
          isSticky,
          el
        };

        const foundCurrentStickyElem = findNextStickyAfterFalse(Object.values(this.stickyList))?.el || null;

        if (foundCurrentStickyElem) {
          this.headerStickyElemHeight = null;
          this.$nextTick(() => {
            this.headerStickyElemHeight = Number(foundCurrentStickyElem.offsetHeight) || null;
          });
        }

        if (idx === 0) {
          this.$emit('triggerSticky', isSticky);
        }
      },
      handleStickyChange (isSticky, idx) {
        if (idx === 0) {
          this.childIsSticky = isSticky;
          this.$emit('triggerSticky', isSticky);
        }
      },
      isActive (choice) {
        return this.selected && choice[this.idAttr] === this.selected[this.idAttr];
      },
      choiceWithChild (choice) {
        return choice[this.childKey] && choice[this.childKey].length > 0;
      },
      activateParentChoice (item) {
        if (this.parentIsOption) {
          this.activateChoice(item);
        }
      },
      activateChoice (item) {
        if (this.isAllowedToActivate(item)) {
          this.$emit('activate', item);
        }
      },
      isAllowedToActivate (item) {
        return !item.notAllowedForSelect;
      },
      getHeaderClassName (choice) {
        return `m-select-control-tree-level__header-${this._uid}-${choice[this.idAttr]}`;
      },
      getStickyTriggerClass (index) {
        return `m-select-control-tree-level__header-sticky-trigger-${this._uid}-${index}`;
      },
      calcHeaderOffset () {
        if (!this.headerClassName) {
          return;
        }

        this._$nextFrame(() => {
          const parentHeader = this.$el.parentElement.querySelector(`.${this.headerClassName}`);
          if (!parentHeader) {
            return;
          }
          this.parentHeaderElHeight = parentHeader.offsetHeight;
        });
      }
    }
  };
</script>

<style>
  .m-select-control-tree-level {
    --sticky-top-offset: 0px;
    --sticky-z-index: 0;
  }

  .m-select-control-tree-level__sticky {
    position: sticky;
    top: var(--sticky-top-offset);
    z-index: var(--sticky-z-index);
  }
</style>
